import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ref, deleteObject } from "firebase/storage";
import storage from "../firebase.js";

const BASE_URL = "https://us-central1-workexpress-1ffff.cloudfunctions.net/app";

const Profile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: initialUser } = location.state || {};
  const [user, setUser] = useState(initialUser);
  const [isSaving, setIsSaving] = useState(false);
  const DEFAULT_IMAGE = "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png";

  if (!user) {
    return (
      <div className="error">
        <p>No user data available</p>
        <button onClick={() => navigate("/customers")}>Back to Customers</button>
        <style>{`
          .error {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            color: #dc2626;
          }
          button {
            margin-top: 10px;
            padding: 8px 16px;
            background: #3b82f6;
            color: #fff;
            border: none;
            border-radius: 4px;
            cursor: pointer;
          }
          button:hover {
            background: #2563eb;
          }
        `}</style>
      </div>
    );
  }

  const imageSrc = user.image
    ? user.image.startsWith("data:image")
      ? user.image
      : `data:image/jpeg;base64,${user.image}`
    : DEFAULT_IMAGE;

  const lastSeenDate = user.lastSeen?._seconds
    ? new Date(user.lastSeen._seconds * 1000).toLocaleString()
    : "N/A";

  const handleDeleteProfilePicture = async () => {
    if (!window.confirm("Are you sure you want to delete the profile picture? It will be replaced with a default image.")) {
      return;
    }

    try {
      setIsSaving(true);
      
      // Delete from Firebase Storage if it's a Firebase Storage URL
      const profilePictureUrl = user.image;
      if (profilePictureUrl && profilePictureUrl.startsWith("https://firebasestorage.googleapis.com")) {
        try {
          // Extract the path from the Firebase Storage URL
          const urlParts = profilePictureUrl.split("/o/");
          if (urlParts.length > 1) {
            const pathPart = urlParts[1].split("?")[0];
            const decodedPath = decodeURIComponent(pathPart);
            const storageRef = ref(storage, decodedPath);
            await deleteObject(storageRef);
          }
        } catch (storageError) {
          console.warn("Error deleting from Firebase Storage:", storageError);
          // Continue even if storage deletion fails
        }
      }

      // Update user in Firestore
      const response = await fetch(`${BASE_URL}/users/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          image: DEFAULT_IMAGE
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update profile picture");
      }

      const updatedUser = await response.json();
      setUser(updatedUser);
      alert("Profile picture deleted successfully");
    } catch (error) {
      console.error("Error deleting profile picture:", error);
      alert("Failed to delete profile picture. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="user-profile">
      <style>{`
        .user-profile {
          width: 100%;
          padding: 2rem;
          box-sizing: border-box;
          background: var(--bg-tertiary, #f9fafb);
          border-radius: 8px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0,0,0,0.05));
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 15px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
          margin-bottom: 20px;
        }

        h2 {
          color: var(--text-primary, #1f2937);
          font-size: 20px;
          margin: 0;
        }

        .back-btn {
          padding: 8px 16px;
          background: #3b82f6;
          color: #fff;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .back-btn:hover {
          background: #2563eb;
        }

        .delete-pic-btn {
          padding: 8px 16px;
          background: #dc2626;
          color: #fff;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .delete-pic-btn:hover:not(:disabled) {
          background: #b91c1c;
        }

        .delete-pic-btn:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .profile-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .profile-item {
          display: flex;
          align-items: center;
          padding: 15px 20px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
          transition: background 0.2s;
        }

        .profile-item:hover {
          background: var(--bg-hover, #f1f5f9);
        }

        .label {
          flex: 0 0 250px;
          color: var(--text-secondary, #4a5568);
          font-weight: 600;
          font-size: 15px;
        }

        .value {
          flex: 1;
          color: var(--text-primary, #374151);
          font-size: 15px;
          word-wrap: break-word;
        }

        .profile-pic {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          object-fit: cover;
          margin-right: 12px;
        }

        .favorites-list {
          padding-left: 20px;
          margin: 5px 0;
        }

        .status {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          display: inline-block;
        }

        .status.active {
          background: #dcfce7;
          color: #166534;
        }

        .status.inactive {
          background: #fee2e2;
          color: #991b1b;
        }

        @media (max-width: 768px) {
          .user-profile {
            padding: 1rem;
          }

          .profile-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            padding: 12px;
          }
          
          .label {
            flex: none;
            font-size: 14px;
          }

          .value {
            font-size: 14px;
          }
        }
      `}</style>

      <div className="header">
        <h2>User Profile</h2>
        <div style={{ display: "flex", gap: "10px" }}>
          {user.image && user.image !== DEFAULT_IMAGE && (
            <button 
              className="delete-pic-btn" 
              onClick={handleDeleteProfilePicture}
              disabled={isSaving}
            >
              {isSaving ? "Deleting..." : "Delete Profile Picture"}
            </button>
          )}
          <button className="back-btn" onClick={() => navigate("/customers")}>
            Back
          </button>
        </div>
      </div>

      <ul className="profile-list">
        <li className="profile-item">
          <span className="label">Profile Picture</span>
          <div className="value" style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <img
              src={imageSrc}
              alt="Profile"
              className="profile-pic"
              onError={(e) => {
                e.target.src = DEFAULT_IMAGE;
              }}
            />
          </div>
        </li>
        <li className="profile-item">
          <span className="label">Name</span>
          <span className="value">{user.name || "N/A"}</span>
        </li>
        <li className="profile-item">
          <span className="label">Email</span>
          <span className="value">{user.email || "N/A"}</span>
        </li>
        <li className="profile-item">
          <span className="label">Phone</span>
          <span className="value">{user.phone || "N/A"}</span>
        </li>
        <li className="profile-item">
          <span className="label">Gender</span>
          <span className="value">{user.gender || "N/A"}</span>
        </li>
        <li className="profile-item">
          <span className="label">Date of Birth</span>
          <span className="value">{user.dob || "N/A"}</span>
        </li>
        <li className="profile-item">
          <span className="label">Address</span>
          <span className="value">{user.address || "N/A"}</span>
        </li>
        <li className="profile-item">
          <span className="label">City</span>
          <span className="value">{user.city || "N/A"}</span>
        </li>
        <li className="profile-item">
          <span className="label">Coordinates</span>
          <span className="value">
            {user.latitude && user.longitude
              ? `${user.latitude}, ${user.longitude}`
              : "N/A"}
          </span>
        </li>

        <li className="profile-item">
          <span className="label">Expo Push Token</span>
          <span className="value">{user.expoPushToken || "N/A"}</span>
        </li>
        <li className="profile-item">
          <span className="label">Last Seen</span>
          <span className="value">{lastSeenDate}</span>
        </li>
        <li className="profile-item">
          <span className="label">Device Type</span>
          <span className="value">{user.deviceType && user.deviceType.trim() !== '' ? user.deviceType : "N/A"}</span>
        </li>
        <li className="profile-item">
          <span className="label">Status</span>
          <span className="value">
            <span className={`status ${user.active ? "active" : "inactive"}`}>
              {user.active ? "Active" : "Inactive"}
            </span>
          </span>
        </li>
      </ul>
    </div>
  );
};

export default Profile;